import React, { useEffect, useState } from "react";
import { Alert, Button, Card, Form, Select, Spin, Switch, Tabs, Typography } from "antd";
import { useTranslation } from "react-i18next";
import { generalErrorAlert } from "../../../commons/GeneralErrorAlert";
import { Success } from "../../../commons/Messages/Messages";
import { isAuthenticated } from "../../../commons/Auth/auth";
import ChannelSettings from "./ChannelSettings";
import TemplateSettings from "./TemplateSettings";
import CustomerConsent from "./CustomerConsent";
import MessageLog from "./MessageLog";
import { getSettings, saveSettings } from "./api";
import "./notifications.css";

const OFFSET_OPTIONS = [1, 2, 3, 4, 6, 12, 24, 48, 72];
const MAX_OFFSETS = 3;

const AppointmentSettings = ({ settings, onSaved }) => {
  const { t } = useTranslation("dashboard");
  const [form] = Form.useForm();
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    form.setFieldsValue(settings.appointments);
  }, [settings]);

  const save = async values => {
    setBusy(true);
    try {
      const res = await saveSettings({ appointments: values });
      Success(t("Settings saved"));
      onSaved(res.data.data);
    } catch (error) {
      generalErrorAlert(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Form form={form} layout="vertical" onFinish={save}>
      <Card size="small" className="nt-card">
        <Form.Item
          name="confirmation"
          valuePropName="checked"
          label={t("Confirm appointments to the customer")}
          extra={t(
            "When an appointment with a customer is booked, or its time or place changes, the customer gets a confirmation with the time window, address and technician."
          )}
        >
          <Switch />
        </Form.Item>
        <Form.Item
          name="reminderOffsets"
          label={t("Reminders before the appointment")}
          extra={t(
            'Up to three reminders, e.g. 24 hours before ("we\'re coming tomorrow between 8 and 10"). No messages are sent between 21:00 and 07:00. A moved appointment is reminded again.'
          )}
          rules={[
            {
              validator: (_, value) =>
                (value || []).length <= MAX_OFFSETS
                  ? Promise.resolve()
                  : Promise.reject(new Error(t("At most three reminders.")))
            }
          ]}
        >
          <Select
            mode="multiple"
            allowClear
            options={OFFSET_OPTIONS.map(h => ({
              value: h,
              label: t("{{count}} hours before", { count: h })
            }))}
          />
        </Form.Item>
      </Card>
      <div className="nt-actions">
        <Button type="primary" htmlType="submit" loading={busy}>
          {t("Save")}
        </Button>
      </div>
    </Form>
  );
};

const MessagingSettings = () => {
  const { t } = useTranslation("dashboard");
  const isOwner = Number(isAuthenticated()?.role) === 2;
  const [settings, setSettings] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOwner) return;
    getSettings()
      .then(res => setSettings(res.data.data))
      .catch(err => {
        setError(err);
        generalErrorAlert(err);
      });
  }, []);

  const tabs = [];
  if (settings) {
    tabs.push(
      {
        key: "channels",
        label: t("Channels"),
        children: <ChannelSettings settings={settings} onSaved={setSettings} />
      },
      {
        key: "appointments",
        label: t("Appointments"),
        children: <AppointmentSettings settings={settings} onSaved={setSettings} />
      },
      {
        key: "templates",
        label: t("Templates"),
        children: <TemplateSettings settings={settings} onSaved={setSettings} />
      }
    );
  }
  tabs.push(
    { key: "customers", label: t("Customer consent"), children: <CustomerConsent /> },
    { key: "log", label: t("Sent messages"), children: <MessageLog /> }
  );

  return (
    <div className="notifications-page">
      <Typography.Title level={4} className="nt-title">
        {t("Customer messages")}
      </Typography.Title>
      <Typography.Paragraph type="secondary">
        {t(
          'Appointment confirmations, reminders and "on the way" messages to your customers by email, SMS or WhatsApp.'
        )}
      </Typography.Paragraph>
      {!isOwner && (
        <Alert
          className="nt-gap"
          type="info"
          showIcon
          message={t("Only the company owner can change the channels and templates.")}
        />
      )}
      {isOwner && !settings && !error ? (
        <div className="nt-center">
          <Spin />
        </div>
      ) : (
        <Tabs items={tabs} destroyInactiveTabPane />
      )}
    </div>
  );
};

export default MessagingSettings;
